import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCode2, FileText, FileUp, KeyRound, Loader2, RefreshCw, RotateCcw, Upload, X } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, Esqueleto, Pilula } from '@/componentes/ui/primitivos';

/**
 * Recebimento > Importar XML — do XML da NF-e do fornecedor à NF de compra no
 * ERP, sem passar por outro app.
 *
 * Duas entradas para o mesmo resultado: o arquivo (um ou vários, arrastando)
 * ou a CHAVE, que o WMS baixa do acervo fiscal. Cada item vai para a API um de
 * cada vez e o resultado aparece na hora, linha a linha — com 20 XMLs, quem
 * enviou vê o primeiro "gerada" em segundos em vez de esperar o lote todo.
 *
 * Embaixo, a fila da NFE_FORNECEDORES (de todos os apps), com a NF de compra,
 * a natureza da operação, o PDF (DANFE) e o XML de cada linha.
 *
 * A que falhou mostra o erro e o que corrigir. Corrigido o cadastro no ERP,
 * "Tentar de novo" (ou colar a chave / soltar o XML de novo) roda a mesma linha;
 * um XML DIFERENTE, corrigido, vira uma tentativa nova.
 */

type Situacao = 'gerada' | 'ja_existia' | 'erro';

interface Resultado {
  chave: string;
  situacao: Situacao;
  nfCompra: number | null;
  registro: number | null;
  mensagem: string | null;
  empresa: number | null;
  nota: { numero: number | null; serie: string; emitenteNome: string; valor: number | null };
}

interface ItemEnvio {
  id: string;
  rotulo: string;
  corpo: { arquivo: string; xml: string } | { chave: string };
  estado: 'aguardando' | 'enviando' | 'buscando' | 'pronto';
  /** Enquanto o portal fiscal busca a nota na SEFAZ. */
  aviso?: string;
  tentativas?: number;
  resultado?: Resultado;
  erro?: string;
}

interface Importacao {
  registro: number;
  quando: string | null;
  arquivo: string | null;
  chave: string | null;
  fornecedor: string | null;
  nfCompra: number | null;
  situacao: Situacao | 'na_fila';
  erro: string | null;
  empresa: number | null;
  numero: number | null;
  serie: string | null;
  emissao: string | null;
  valor: number | null;
  origem: string | null;
  natureza: string | null;
}

const ROTULO: Record<Situacao | 'na_fila', { texto: string; tom: 'ok' | 'neutro' | 'erro' | 'alerta' }> = {
  gerada: { texto: 'NF de compra gerada', tom: 'ok' },
  ja_existia: { texto: 'já existia', tom: 'neutro' },
  erro: { texto: 'erro', tom: 'erro' },
  na_fila: { texto: 'na fila', tom: 'alerta' },
};

const moeda = (v: number | null): string =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * O que corrigir para a próxima tentativa passar, a partir da mensagem da
 * procedure. Ela diz o que falta, mas não onde se arruma — e quem importa XML
 * nem sempre é quem mexe no cadastro.
 */
function dicaDoErro(mensagem: string | null): string | null {
  const m = (mensagem ?? '').toLowerCase();
  if (m.includes('fornecedor com cnpj'))
    return 'Cadastre o fornecedor (entidade com este CNPJ) no ERP e clique em Tentar de novo.';
  if (m.includes('empresa com cnpj'))
    return 'A nota não é para uma empresa do grupo: confira o destinatário do XML.';
  if (m.includes('operações fiscais') || m.includes('operacoes fiscais') || m.includes('operçoes'))
    return 'Cadastre o CFOP da nota em "Operações Fiscais DANFE" no ERP e clique em Tentar de novo.';
  if (m.includes('situações tributárias') || m.includes('situacoes tributarias'))
    return 'Cadastre a situação tributária (CST) da nota no ERP e clique em Tentar de novo.';
  if (m.includes('ncm')) return 'Há produto sem NCM no XML: peça ao fornecedor o XML corrigido e envie de novo.';
  if (m.includes('parcelas')) return 'As parcelas do XML não fecham: confira a cobrança (dup) com o fornecedor.';
  if (m.includes('qlote') || m.includes('lote'))
    return 'As quantidades de lote não batem com as do item: peça o XML corrigido e envie de novo.';
  if (m.includes('truncad') || m.includes('converter'))
    return 'Algum campo do XML não cabe no ERP. Corrija o arquivo e envie de novo (o XML novo vira outra tentativa).';
  return null;
}

function dataHora(iso: string | null, comHora = true): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', comHora
        ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Lê o arquivo em UTF-8, a menos que o próprio XML diga outra codificação. */
async function lerArquivo(arquivo: File): Promise<string> {
  const bytes = new Uint8Array(await arquivo.arrayBuffer());
  const inicio = new TextDecoder('latin1').decode(bytes.slice(0, 200));
  const cod = /encoding\s*=\s*["']([^"']+)["']/i.exec(inicio)?.[1]?.toLowerCase() ?? 'utf-8';
  try {
    return new TextDecoder(cod).decode(bytes);
  } catch {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

export function PainelImportarXml({ empresa }: { empresa: number | null }): JSX.Element {
  const cliente = useQueryClient();
  const { temPermissao } = useSessao();
  const pode = temPermissao('recebimento.xml');

  const [itens, definirItens] = React.useState<ItemEnvio[]>([]);
  const [chaves, definirChaves] = React.useState('');
  const [arrastando, definirArrastando] = React.useState(false);
  const [dias, definirDias] = React.useState(7);
  const [soErro, definirSoErro] = React.useState(false);
  const processando = React.useRef(false);
  const entradaArquivo = React.useRef<HTMLInputElement>(null);

  const lista = useQuery<{ importacoes: Importacao[] }>({
    queryKey: ['xml-compra', empresa, dias],
    queryFn: () => api.get('/api/recebimentos/xml-compra', { dias, ...(empresa ? { empresa } : {}) }),
    staleTime: 15_000,
  });

  const atualizar = (id: string, mudanca: Partial<ItemEnvio>): void =>
    definirItens((atual) => atual.map((i) => (i.id === id ? { ...i, ...mudanca } : i)));

  /*
    Um por vez: a API serializa de qualquer jeito, e assim cada linha mostra o
    resultado quando chega. O que entra enquanto a fila anda vai para o fim
    dela, e o mesmo laço pega.
  */
  const pendentes = React.useRef<ItemEnvio[]>([]);
  const drenar = React.useCallback(async () => {
    if (processando.current) return;
    processando.current = true;
    try {
      for (let item = pendentes.current.shift(); item; item = pendentes.current.shift()) {
        const id = item.id;
        atualizar(id, { estado: 'enviando' });
        try {
          const resposta = await api.post<Resultado | { pendente: true; mensagem: string }>(
            '/api/recebimentos/xml-compra',
            { ...item.corpo, empresa },
          );
          if ('pendente' in resposta) {
            /*
              Não estava no acervo e o portal fiscal foi buscar na SEFAZ.
              Pergunta de novo em 20 s — a API acompanha o mesmo pedido, não
              abre outro —, por até 15 minutos.
            */
            const tentativas = (item.tentativas ?? 0) + 1;
            if (tentativas > 45) {
              atualizar(id, { estado: 'pronto', erro: 'A SEFAZ não devolveu a nota a tempo. Tente de novo mais tarde.' });
            } else {
              const deNovo = { ...item, tentativas };
              atualizar(id, { estado: 'buscando', aviso: resposta.mensagem, tentativas });
              setTimeout(() => {
                pendentes.current.push(deNovo);
                void drenar();
              }, 20_000);
            }
          } else {
            atualizar(id, { estado: 'pronto', resultado: resposta });
          }
        } catch (err) {
          atualizar(id, { estado: 'pronto', erro: err instanceof ErroHttp ? err.message : 'Falha ao enviar.' });
        }
      }
    } finally {
      processando.current = false;
      void cliente.invalidateQueries({ queryKey: ['xml-compra'] });
    }
  }, [cliente, empresa]);

  const enfileirar = (novos: ItemEnvio[]): void => {
    if (novos.length === 0) return;
    definirItens((atual) => [...novos, ...atual]);
    pendentes.current.push(...novos);
    void drenar();
  };

  const adicionarArquivos = async (arquivos: FileList | File[]): Promise<void> => {
    const xmls = Array.from(arquivos).filter((a) => /\.xml$/i.test(a.name) || a.type.includes('xml'));
    const novos: ItemEnvio[] = [];
    for (const a of xmls) {
      novos.push({
        id: `${a.name}-${a.size}-${Math.random().toString(36).slice(2)}`,
        rotulo: a.name,
        corpo: { arquivo: a.name, xml: await lerArquivo(a) },
        estado: 'aguardando',
      });
    }
    enfileirar(novos);
  };

  const listaDeChaves = Array.from(new Set(chaves.match(/\d{44}/g) ?? []));
  const enviarChaves = (): void => {
    enfileirar(
      listaDeChaves.map((c) => ({
        id: `${c}-${Math.random().toString(36).slice(2)}`,
        rotulo: c,
        corpo: { chave: c },
        estado: 'aguardando' as const,
      })),
    );
    definirChaves('');
  };

  const [reprocessando, definirReprocessando] = React.useState<number | null>(null);
  const [erroReprocesso, definirErroReprocesso] = React.useState<string | null>(null);
  const reprocessar = async (registro: number): Promise<void> => {
    definirReprocessando(registro);
    definirErroReprocesso(null);
    try {
      const r = await api.post<{ situacao: Situacao; nfCompra: number | null; mensagem: string | null }>(
        `/api/recebimentos/xml-compra/${registro}/reprocessar`,
      );
      // O mesmo resultado também na lista de "Enviados agora", se a linha veio de lá.
      definirItens((atual) =>
        atual.map((i) =>
          i.resultado?.registro === registro
            ? { ...i, resultado: { ...i.resultado, ...r } }
            : i,
        ),
      );
      if (r.situacao === 'erro') definirErroReprocesso(`Registro ${registro}: ${r.mensagem ?? 'falhou de novo.'}`);
    } catch (err) {
      definirErroReprocesso(err instanceof ErroHttp ? err.message : 'Falha ao reprocessar.');
    } finally {
      definirReprocessando(null);
      void cliente.invalidateQueries({ queryKey: ['xml-compra'] });
    }
  };

  const todas = lista.data?.importacoes ?? [];
  const comErro = todas.filter((l) => l.situacao === 'erro').length;
  const importacoes = soErro ? todas.filter((l) => l.situacao === 'erro') : todas;

  return (
    <div className="space-y-3">
      {pode ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* ARQUIVOS */}
          <Cartao
            className={cn(
              'flex flex-col items-center justify-center gap-2 border-2 border-dashed p-5 text-center transition-colors',
              arrastando ? 'border-primaria bg-primaria-suave' : 'border-borda',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              definirArrastando(true);
            }}
            onDragLeave={() => definirArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              definirArrastando(false);
              void adicionarArquivos(e.dataTransfer.files);
            }}
          >
            <FileUp className="h-8 w-8 text-primaria" />
            <p className="text-[14px] font-semibold">Arraste os XMLs das notas aqui</p>
            <p className="text-[12px] text-textoFraco">Um ou vários arquivos .xml de NF-e do fornecedor</p>
            <input
              ref={entradaArquivo}
              type="file"
              accept=".xml,text/xml,application/xml"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void adicionarArquivos(e.target.files);
                e.target.value = '';
              }}
            />
            <Botao variante="primaria" tamanho="pequeno" onClick={() => entradaArquivo.current?.click()}>
              <Upload className="h-4 w-4" />
              Escolher arquivos
            </Botao>
          </Cartao>

          {/* CHAVES */}
          <Cartao className="flex flex-col gap-2 p-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primaria" />
              <p className="text-[14px] font-semibold">Busque pela chave da NF-e</p>
            </div>
            <textarea
              value={chaves}
              onChange={(e) => definirChaves(e.target.value)}
              rows={3}
              placeholder="35261047747969000194550010020789491348974157"
              className="min-h-[76px] w-full resize-y rounded-lg border border-borda bg-superficie px-2.5 py-2 font-mono text-[12.5px] outline-none focus:border-primaria"
            />
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-textoFraco">
                {listaDeChaves.length === 0
                  ? 'Nenhuma chave válida'
                  : `${listaDeChaves.length} chave${listaDeChaves.length > 1 ? 's' : ''}`}
              </span>
              <Botao
                className="ml-auto"
                variante="primaria"
                tamanho="pequeno"
                disabled={listaDeChaves.length === 0}
                onClick={enviarChaves}
              >
                Buscar e gerar NF de compra
              </Botao>
            </div>
          </Cartao>
        </div>
      ) : (
        <Cartao className="p-3 text-[13px] text-textoFraco">
          Você pode consultar a fila, mas importar XML pede a permissão “Importar XML de NF de compra”.
        </Cartao>
      )}

      {/* ENVIADOS AGORA */}
      {itens.length > 0 ? (
        <Cartao className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-borda px-3 py-2">
            <span className="text-[13px] font-bold">Enviados agora</span>
            <span className="text-[12px] text-textoFraco">
              {itens.filter((i) => i.resultado?.situacao === 'gerada').length} gerada(s) ·{' '}
              {itens.filter((i) => i.erro || i.resultado?.situacao === 'erro').length} com erro
            </span>
            <Botao
              className="ml-auto"
              variante="fantasma"
              tamanho="pequeno"
              disabled={itens.some((i) => i.estado !== 'pronto')}
              onClick={() => definirItens([])}
            >
              <X className="h-4 w-4" />
              Limpar
            </Botao>
          </div>
          <ul className="divide-y divide-borda">
            {itens.map((i) => {
              const r = i.resultado;
              const sit = i.erro ? 'erro' : r?.situacao;
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px]">
                  <span className="w-[150px] shrink-0">
                    {i.estado !== 'pronto' ? (
                      <span className="inline-flex items-center gap-1.5 text-textoFraco">
                        <Loader2 className={cn('h-3.5 w-3.5', i.estado === 'enviando' && 'animate-spin')} />
                        {i.estado === 'enviando' ? 'gerando…' : i.estado === 'buscando' ? 'buscando na SEFAZ' : 'aguardando'}
                      </span>
                    ) : sit ? (
                      <Pilula tom={ROTULO[sit].tom}>{ROTULO[sit].texto}</Pilula>
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    {i.estado === 'buscando' && i.aviso ? (
                      <span className="block text-[12px] text-textoFraco">{i.aviso}</span>
                    ) : null}
                    <span className="block truncate font-medium">
                      {r ? `${r.nota.emitenteNome} · NF ${r.nota.numero ?? '?'}/${r.nota.serie}` : i.rotulo}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-textoFraco">
                      {r ? `${r.chave}${r.empresa ? ` · empresa ${r.empresa}` : ''} · ${moeda(r.nota.valor)}` : ''}
                    </span>
                    {i.erro || (r?.situacao === 'erro' && r.mensagem) ? (
                      <span className="block text-[12px] text-status-divergencia">{i.erro ?? r?.mensagem}</span>
                    ) : null}
                    {r?.situacao === 'erro' && dicaDoErro(r.mensagem) ? (
                      <span className="block text-[12px] font-medium">{dicaDoErro(r.mensagem)}</span>
                    ) : null}
                    {r?.situacao === 'erro' && r.registro && pode ? (
                      <Botao
                        className="mt-1"
                        variante="secundaria"
                        tamanho="pequeno"
                        carregando={reprocessando === r.registro}
                        disabled={reprocessando !== null}
                        onClick={() => void reprocessar(r.registro!)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Tentar de novo
                      </Botao>
                    ) : null}
                  </span>
                  {r?.nfCompra ? (
                    <span className="tabular text-right">
                      <span className="rotulo block">NF compra</span>
                      <span className="font-mono text-[16px] font-bold">{r.nfCompra}</span>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Cartao>
      ) : null}

      {/* FILA DA NFE_FORNECEDORES */}
      <Cartao className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-borda px-3 py-2">
          <span className="text-[13px] font-bold">Últimas importações</span>
          {comErro > 0 || soErro ? (
            <button
              type="button"
              onClick={() => definirSoErro((v) => !v)}
              className={cn(
                'rounded-full px-2.5 py-0.5 text-[12px] font-semibold ring-1',
                soErro
                  ? 'bg-status-divergencia text-white ring-status-divergencia'
                  : 'bg-status-divergenciaFundo text-status-divergencia ring-status-divergencia/30',
              )}
            >
              {soErro ? 'Mostrar todas' : `${comErro} com erro para corrigir`}
            </button>
          ) : null}
          <select
            value={dias}
            onChange={(e) => definirDias(Number(e.target.value))}
            className="ml-auto rounded-md border border-borda bg-superficie px-2 py-1 text-[12.5px]"
          >
            <option value={1}>Hoje</option>
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
          </select>
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => void lista.refetch()} carregando={lista.isFetching}>
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Botao>
        </div>
        {erroReprocesso ? (
          <p className="border-b border-borda bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
            {erroReprocesso}
          </p>
        ) : null}
        {lista.isLoading ? (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3].map((n) => (
              <Esqueleto key={n} className="h-8" />
            ))}
          </div>
        ) : lista.isError ? (
          <div className="p-3">
            <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
          </div>
        ) : importacoes.length === 0 ? (
          <div className="p-3">
            <EstadoVazio icone={<FileUp className="h-7 w-7" />} titulo="Nenhum XML importado no período" />
          </div>
        ) : (
          <div className="max-h-[calc(100vh-420px)] min-h-[240px] overflow-auto">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 z-10 bg-superficie2 text-left text-[11.5px] uppercase tracking-wide text-textoFraco">
                <tr>
                  <th className="px-3 py-2">Quando</th>
                  <th className="px-3 py-2">Fornecedor</th>
                  <th className="px-3 py-2">NF · natureza</th>
                  <th className="px-3 py-2">Emissão</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2">Emp.</th>
                  <th className="px-3 py-2 text-right">NF compra</th>
                  <th className="px-3 py-2">Situação</th>
                  <th className="px-3 py-2">Origem</th>
                  <th className="px-3 py-2">Arquivos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {importacoes.map((l) => (
                  <React.Fragment key={l.registro}>
                    <tr className={cn('align-top hover:bg-superficie2/60', l.situacao === 'erro' && 'bg-status-divergenciaFundo/40')}>
                      <td className="whitespace-nowrap px-3 py-1.5 tabular">{dataHora(l.quando)}</td>
                      <td className="max-w-[260px] px-3 py-1.5">
                        <span className="block truncate" title={l.fornecedor ?? ''}>
                          {l.fornecedor || '—'}
                        </span>
                        <span className="block truncate font-mono text-[10.5px] text-textoFraco" title={l.chave ?? ''}>
                          {l.chave ?? l.arquivo}
                        </span>
                      </td>
                      <td className="max-w-[220px] px-3 py-1.5">
                        <span className="block whitespace-nowrap tabular">
                          {l.numero ? `${l.numero}/${l.serie ?? ''}` : '—'}
                        </span>
                        {l.natureza ? (
                          <span className="block truncate text-[11px] text-textoFraco" title={l.natureza}>
                            {l.natureza}
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 tabular">{dataHora(l.emissao, false)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular">{moeda(l.valor)}</td>
                      <td className="px-3 py-1.5 tabular">{l.empresa ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{l.nfCompra ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <Pilula tom={ROTULO[l.situacao].tom}>{ROTULO[l.situacao].texto}</Pilula>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-textoFraco">{l.origem ?? 'outro app'}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span className="flex gap-1">
                          <a
                            href={`/api/recebimentos/xml-compra/${l.registro}/pdf`}
                            download
                            title="Baixar a DANFE (PDF)"
                            className="inline-flex items-center gap-1 rounded-md border border-borda px-1.5 py-0.5 text-[11px] font-semibold hover:bg-superficie2"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            PDF
                          </a>
                          <a
                            href={`/api/recebimentos/xml-compra/${l.registro}/xml`}
                            download
                            title="Baixar o XML"
                            className="inline-flex items-center gap-1 rounded-md border border-borda px-1.5 py-0.5 text-[11px] font-semibold hover:bg-superficie2"
                          >
                            <FileCode2 className="h-3.5 w-3.5" />
                            XML
                          </a>
                        </span>
                      </td>
                    </tr>
                    {/*
                      O erro na largura toda, logo abaixo da linha: espremido na
                      coluna Situação, a mensagem da procedure e a dica viravam
                      uma torre de 8 linhas e empurravam a grade.
                    */}
                    {l.situacao === 'erro' ? (
                      <tr className="!border-t-0 bg-status-divergenciaFundo/40">
                        <td colSpan={10} className="px-3 pb-2 pt-0">
                          <div className="ml-6 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border-l-4 border-status-divergencia bg-superficie px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-[12.5px] text-status-divergencia">
                                {l.erro ?? 'Não gerou NF de compra.'}
                              </p>
                              {dicaDoErro(l.erro) ? (
                                <p className="text-[12.5px] font-medium">{dicaDoErro(l.erro)}</p>
                              ) : null}
                            </div>
                            {pode ? (
                              <Botao
                                variante="secundaria"
                                tamanho="pequeno"
                                carregando={reprocessando === l.registro}
                                disabled={reprocessando !== null}
                                onClick={() => void reprocessar(l.registro)}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Tentar de novo
                              </Botao>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </div>
  );
}
